import { AlertTriangle, Loader2, Lock } from "lucide-react";
import { MONTHS, fmtSEK, g } from "../revenue/format";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../ui/tooltip";

const fmtPct = (v) => (v == null ? "N/A" : `${v.toFixed(1)}%`);

const DRILLABLE = new Set([
  "tm", "projects", "services", "other",
  "employee_direct", "subcontractor_direct",
  "regular_indirect", "overhead_personnel", "group_overhead", "factoring", "depreciation", "interest",
  "corporate_tax",
]);

function CategoryRow({ label }) {
  return (
    <tr className="bg-secondary/50 border-y border-slate-200" data-testid={`pnl-cat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <td colSpan={15} className="py-1.5 px-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 sticky left-0 bg-slate-50">
        {label}
      </td>
    </tr>
  );
}

function MoneyRow({ line, label, data, kind = "line", note, onDrilldown }) {
  const strong = kind === "subtotal" || kind === "final";
  const final = kind === "final";
  const drillable = DRILLABLE.has(line);
  const deduction = line === "corporate_tax"; // tax is shown as a deduction from PBT
  const cellText = (val) => (val == null ? "—" : deduction && val > 0 ? `−${fmtSEK(val)}` : fmtSEK(val));
  return (
    <tr className={`border-b border-slate-100 ${final ? "bg-[#F8FAFC] border-t-2 border-t-slate-300" : strong ? "bg-slate-50/50" : ""}`} data-testid={`pnl-row-${line}`}>
      <td className="py-1.5 px-3 sticky left-0 bg-white z-10" style={{ backgroundColor: final ? "#F8FAFC" : strong ? "#FAFBFC" : "#fff" }}>
        {drillable ? (
          <button
            className={`text-left text-sm hover:underline ${strong ? "font-bold text-slate-900" : "text-[#0052CC]"}`}
            onClick={() => onDrilldown(line, 1)}
            data-testid={`pnl-open-${line}`}
          >
            {label}
          </button>
        ) : (
          <span className={`text-sm ${strong ? "font-bold text-slate-900" : "text-slate-700"}`}>{label}</span>
        )}
        {note && <span className="text-[10px] text-slate-400 block">{note}</span>}
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cell = g(data.months, m) || {};
        const val = cell[line];
        return (
          <td key={m} className={`py-1.5 px-1 text-right ${cell.closed ? "bg-slate-100/70" : ""}`} data-testid={`pnl-cell-${line}-${m}`}>
            <button
              className={`h-8 w-full pr-1.5 text-right text-sm ${strong ? "font-bold text-slate-900" : "text-slate-700"} ${(val != null && val < 0) || (deduction && val != null && val > 0) ? "text-[#B91C1C]" : ""} ${drillable ? "hover:bg-secondary/50 rounded" : "cursor-default"}`}
              onClick={drillable ? () => onDrilldown(line, m) : undefined}
              data-testid={`pnl-cell-${line}-${m}-btn`}
            >
              {cellText(val)}
            </button>
          </td>
        );
      })}
      <td className="py-1.5 px-2 text-right text-sm text-slate-500" data-testid={`pnl-ytd-${line}`}>
        {data.ytd ? cellText(data.ytd[line]) : "—"}
      </td>
      <td className={`py-1.5 px-2 text-right text-sm font-bold ${final ? "text-[#0052CC]" : "text-slate-900"}`} data-testid={`pnl-fy-${line}`}>
        {cellText(data.fy[line])}
      </td>
    </tr>
  );
}

function RatioRow({ line, label, data, onDrilldown }) {
  return (
    <tr className="border-b border-slate-100" data-testid={`pnl-row-${line}`}>
      <td className="py-1.5 px-3 sticky left-0 bg-white z-10">
        <span className="text-sm italic text-slate-500">{label}</span>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cell = g(data.months, m) || {};
        const target = g(data.targets?.[line] || {}, m);
        return (
          <td key={m} className="py-1.5 px-1 text-right" data-testid={`pnl-cell-${line}-${m}`}>
            <div className="h-8 pr-1.5 flex flex-col items-end justify-center">
              <span className="text-sm italic text-slate-600">{fmtPct(cell[line])}</span>
              {target != null && <span className="text-[9px] text-slate-400 leading-none">tgt {target}%</span>}
            </div>
          </td>
        );
      })}
      <td className="py-1.5 px-2 text-right text-sm italic text-slate-500" data-testid={`pnl-ytd-${line}`}>
        {data.ytd ? fmtPct(data.ytd[line]) : "—"}
      </td>
      <td className="py-1.5 px-2 text-right text-sm italic font-semibold text-slate-600" data-testid={`pnl-fy-${line}`}>
        {fmtPct(data.fy[line])}
      </td>
    </tr>
  );
}

function PlaceholderRow({ line, label, note }) {
  return (
    <tr className="border-b border-slate-100" data-testid={`pnl-row-${line}`}>
      <td className="py-1.5 px-3 sticky left-0 bg-white z-10">
        <span className="text-sm text-slate-400">{label}</span>
      </td>
      <td colSpan={14} className="py-1.5 px-2 text-left text-xs text-slate-400" data-testid={`pnl-placeholder-${line}`}>
        {note}
      </td>
    </tr>
  );
}

export default function PnlGrid({ data, loading, onDrilldown }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="pnl-grid">
      {loading && (
        <div className="py-16 flex justify-center" data-testid="pnl-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      )}
      {!loading && data && (
        <table className="w-full min-w-[1300px] border-collapse">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-secondary/50 min-w-56 z-10">P&L Line</th>
              {MONTHS.map((label, i) => {
                const m = i + 1;
                const cell = g(data.months, m) || {};
                return (
                  <th key={label} className={`text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-1 w-24 ${cell.closed ? "text-slate-400 bg-slate-100/70" : "text-slate-500"}`}>
                    <div className="flex items-center justify-end gap-1 pr-1.5">
                      {cell.closed && <Lock className="h-3 w-3" />}
                      {cell.actual_source === "ACCOUNTING_IMPORT" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex items-center rounded bg-[#0052CC] text-white text-[9px] font-bold px-1 py-px" data-testid={`pnl-month-actual-${m}`}>
                              ACT
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="font-semibold mb-1">{label} — accounting actuals</div>
                            {(cell.warnings || []).map((w, idx) => <div key={idx}>{w}</div>)}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {cell.status === "ACTUAL" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex items-center rounded bg-[#0052CC] text-white text-[9px] font-bold px-1 py-px" data-testid={`cpnl-month-actual-${m}`}>
                              ACT
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{label} — every legal entity is actual (closed or committed import)</TooltipContent>
                        </Tooltip>
                      )}
                      {cell.status === "MIXED" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex items-center rounded bg-[#B45309] text-white text-[9px] font-bold px-1 py-px" data-testid={`cpnl-month-mixed-${m}`}>
                              MIX
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{label} — mixed: some legal entities are still forecast/uncommitted; the consolidated month is not fully actual</TooltipContent>
                        </Tooltip>
                      )}
                      {cell.incomplete && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex" data-testid={`pnl-month-warning-${m}`}>
                              <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="font-semibold mb-1">{label} — incomplete source data</div>
                            {(cell.warnings || []).map((w, idx) => <div key={idx}>{w}</div>)}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {label}
                    </div>
                  </th>
                );
              })}
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-24">YTD</th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28">Expected FY</th>
            </tr>
          </thead>
          <tbody>
            <CategoryRow label="Sales" />
            <MoneyRow line="tm" label="Time & Material" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="projects" label="Projects" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="services" label="Services" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="other" label="Other Revenue" data={data} onDrilldown={onDrilldown} />
            {data.is_group_company && (
              <MoneyRow line="pass_through_revenue" label="Customer Pass-through Revenue" data={data} note="Calculated — operating companies via Group" onDrilldown={onDrilldown} />
            )}
            {data.is_group_company && (
              <MoneyRow line="overhead_recharge" label="Group Overhead Recharge" data={data} note="Calculated — intercompany recharge" onDrilldown={onDrilldown} />
            )}
            <MoneyRow line="total_sales" label="Total Sales" kind="subtotal" data={data} onDrilldown={onDrilldown} />

            <CategoryRow label="Revenue-related Costs" />
            <MoneyRow
              line="factoring"
              label="Factoring (6064)"
              data={data}
              note={data.factoring_present ? null : "No 6064 account — N/A"}
              onDrilldown={onDrilldown}
            />

            <CategoryRow label="Direct Costs" />
            <MoneyRow line="employee_direct" label="Employee Consultants" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="subcontractor_direct" label="Subcontractors" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="total_direct" label="Total Direct Costs" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            {data.is_group_company && (
              <MoneyRow line="pass_through_cost" label="Customer Pass-through Cost" data={data} note="Calculated — back-to-back, zero margin" onDrilldown={onDrilldown} />
            )}
            <RatioRow line="direct_cost_ratio" label="Direct Cost Ratio" data={data} />

            <CategoryRow label="Gross Result" />
            <MoneyRow line="gross_profit" label="Gross Profit" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            <RatioRow line="gross_margin" label="Gross Margin" data={data} />

            <CategoryRow label="Indirect Costs" />
            <MoneyRow line="regular_indirect" label="Regular Indirect Costs" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="overhead_personnel" label="Overhead Personnel Costs" data={data} note="Employee + Overhead — canonical PAY engine" onDrilldown={onDrilldown} />
            <MoneyRow line="group_overhead" label="Group Overhead (6591)" data={data} onDrilldown={onDrilldown} />
            <MoneyRow line="depreciation" label="Depreciation" data={data} note="Not classified — open item" onDrilldown={onDrilldown} />
            <MoneyRow line="interest" label="Interest" data={data} note="Not classified — open item" onDrilldown={onDrilldown} />
            <MoneyRow line="total_indirect" label="Total Indirect Costs" kind="subtotal" data={data} onDrilldown={onDrilldown} />
            <RatioRow line="indirect_cost_ratio" label="Indirect Cost Ratio" data={data} />

            <CategoryRow label="Result" />
            <MoneyRow line="pbt" label="Profit Before Corporate Tax" kind="final" data={data} onDrilldown={onDrilldown} />
            <RatioRow line="net_margin" label="Net Margin (pre-tax)" data={data} />
            <MoneyRow line="corporate_tax" label="Corporate Tax" data={data} note="Annual calculation — shown at FY level (Build 7)" onDrilldown={onDrilldown} />
            <MoneyRow line="profit_after_tax" label="Profit After Tax" kind="final" data={data} note="PBT − Corporate Tax (annual)" onDrilldown={onDrilldown} />
          </tbody>
        </table>
      )}
    </div>
  );
}
